import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Ticket, Video, Upload, Check, RefreshCw, Trophy } from 'lucide-react';
import { gameshowApi, GameShowApiError, type Tape } from '@/services/gameshowApi';

// ── /show-submit/:showId ──────────────────────────────────────────────
// Performer self-tape submission for a Video Battle. FREE — no payment
// anywhere. Upload a tape (file → presigned PUT → register with a title);
// an existing submission shows with a Replace option. Registration-gated
// like the audience page.

const PAGE_BG = 'radial-gradient(120% 70% at 50% -6%, #2a1c3c 0%, #16110f 52%, #0b0908 100%)';
const GOLD = '#ffd600';
const GREEN = '#8cc63f';
const REC = '#ff4d6a';

const kicker: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  fontWeight: 600,
};

// PUT a file to a presigned S3 URL with progress (mirrors the quick-tools flow).
function uploadToS3(file: File, uploadUrl: string, onProgress: (p: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    });
    xhr.addEventListener('load', () => {
      if (xhr.status === 200) resolve();
      else reject(new Error(`Upload failed: ${xhr.status}`));
    });
    xhr.addEventListener('error', () => reject(new Error('Upload failed')));
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
    xhr.send(file);
  });
}

// Module-level so it isn't re-created each render (which would remount the form
// and drop focus from the title input on every keystroke).
const Wrap: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      minHeight: '100vh',
      background: PAGE_BG,
      color: 'var(--hj-ink)',
      fontFamily: 'var(--hj-font-ui)',
      display: 'flex',
      justifyContent: 'center',
      padding: '18px 14px 40px',
    }}
  >
    <div style={{ width: '100%', maxWidth: 460, display: 'flex', flexDirection: 'column', gap: 16 }}>{children}</div>
  </div>
);

const GameShowSubmitPage: React.FC = () => {
  const { showId } = useParams<{ showId: string }>();

  const [tape, setTape] = useState<Tape | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [registrationRequired, setRegistrationRequired] = useState(false);
  const [gateProductId, setGateProductId] = useState<string | null>(null);
  const [formatMismatch, setFormatMismatch] = useState(false);

  // Compose/replace form state.
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!showId) return;
    setLoading(true);
    try {
      const t = await gameshowApi.myTape(showId);
      setTape(t);
      setError(null);
      setRegistrationRequired(false);
      setFormatMismatch(false);
      if (!t) setEditing(true); // no tape yet → drop straight into the upload form
    } catch (e) {
      if (e instanceof GameShowApiError && e.code === 'REGISTRATION_REQUIRED') {
        setRegistrationRequired(true);
        const pid = e.body?.productId;
        setGateProductId(typeof pid === 'string' ? pid : null);
      } else if (e instanceof GameShowApiError && e.code === 'FORMAT_MISMATCH') {
        setFormatMismatch(true);
      } else {
        setError(e instanceof Error ? e.message : 'Could not load this show.');
      }
    } finally {
      setLoading(false);
    }
  }, [showId]);

  useEffect(() => {
    load();
  }, [load]);

  const pickFile = (f: File | null) => {
    setFile(f);
    setFormError(null);
    // Prefill the title from the filename (minus extension) if empty.
    if (f && !title.trim()) setTitle(f.name.replace(/\.[^.]+$/, '').slice(0, 80));
  };

  const submit = async () => {
    if (!showId || busy) return;
    const cleanTitle = title.trim();
    if (!cleanTitle) {
      setFormError('Give your tape a title.');
      return;
    }
    if (cleanTitle.length > 80) {
      setFormError('Title must be 80 characters or fewer.');
      return;
    }
    if (!file) {
      setFormError('Choose a video file to upload.');
      return;
    }
    setBusy(true);
    setProgress(0);
    setFormError(null);
    try {
      const { uploadUrl, key } = await gameshowApi.tapeUploadUrl(showId, file.name, file.type || 'video/mp4');
      await uploadToS3(file, uploadUrl, setProgress);
      const saved = await gameshowApi.submitTape(showId, cleanTitle, key);
      setTape(saved);
      setEditing(false);
      setFile(null);
      setProgress(0);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Could not submit your tape.');
    } finally {
      setBusy(false);
    }
  };

  const startReplace = () => {
    setTitle(tape?.title || '');
    setFile(null);
    setProgress(0);
    setFormError(null);
    setEditing(true);
  };

  // ── gate / status screens ──
  if (loading) {
    return (
      <Wrap>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: 'rgba(255,255,255,.6)', padding: '60px 0' }}>
          <Loader2 size={18} className="animate-spin" /> Loading…
        </div>
      </Wrap>
    );
  }

  if (registrationRequired) {
    return (
      <Wrap>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 15, textAlign: 'center', padding: '40px 14px' }}>
          <span
            style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,214,0,.12)',
              border: '1px solid rgba(255,214,0,.4)',
            }}
          >
            <Ticket size={26} color={GOLD} />
          </span>
          <div style={{ ...kicker, letterSpacing: '0.2em', color: GOLD }}>Registered performers only</div>
          <h2 className="font-hj-display" style={{ fontSize: 27, margin: 0, color: '#fff', lineHeight: 1.1 }}>
            Register for this show to submit
          </h2>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,.72)', maxWidth: 320, lineHeight: 1.45, margin: 0 }}>
            Submitting a tape is free — you just need to be registered for the show.
          </p>
          {gateProductId && (
            <Link
              to={`/course/${gateProductId}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 4,
                padding: '12px 22px',
                borderRadius: 999,
                background: GOLD,
                color: '#150e20',
                fontSize: 14,
                fontWeight: 700,
                textDecoration: 'none',
              }}
            >
              <Ticket size={16} /> Register now
            </Link>
          )}
        </div>
      </Wrap>
    );
  }

  if (formatMismatch) {
    return (
      <Wrap>
        <div style={{ textAlign: 'center', color: 'rgba(255,255,255,.72)', fontSize: 14, padding: '50px 14px' }}>
          This show isn't a video battle — there's nothing to submit here.
        </div>
      </Wrap>
    );
  }

  if (error) {
    return (
      <Wrap>
        <div style={{ textAlign: 'center', color: '#ff9db3', fontSize: 14, padding: '50px 0' }}>{error}</div>
      </Wrap>
    );
  }

  return (
    <Wrap>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <Trophy size={16} color={GOLD} />
        <span style={{ ...kicker, color: GOLD }}>Video Battle · self-tape</span>
      </div>
      <h1 className="font-hj-display" style={{ fontSize: 30, margin: 0, color: '#fff', lineHeight: 1.05 }}>
        Submit your tape
      </h1>
      <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,.66)', margin: 0, lineHeight: 1.45 }}>
        Free to enter. Upload one self-tape — the host casts the bracket and the audience votes head-to-head.
      </p>

      {/* existing submission */}
      {tape && !editing && (
        <div
          style={{
            borderRadius: 16,
            overflow: 'hidden',
            border: '1px solid rgba(140,198,63,.4)',
            background: 'rgba(140,198,63,.06)',
          }}
        >
          <div style={{ background: '#000', aspectRatio: '16 / 9' }}>
            {tape.videoUrl ? (
              <video src={tape.videoUrl} controls playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }} />
            ) : null}
          </div>
          <div style={{ padding: 15, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Check size={15} color={GREEN} />
              <span style={{ ...kicker, color: GREEN }}>
                {tape.status === 'cast' ? 'Cast into the bracket' : 'Submitted'}
              </span>
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--hj-ink)' }}>{tape.title}</div>
            <button
              onClick={startReplace}
              style={{
                alignSelf: 'flex-start',
                cursor: 'pointer',
                border: '1px solid rgba(255,255,255,.2)',
                background: 'rgba(255,255,255,.05)',
                color: 'var(--hj-ink)',
                borderRadius: 11,
                padding: '10px 16px',
                fontFamily: 'var(--hj-font-ui)',
                fontWeight: 700,
                fontSize: 13.5,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <RefreshCw size={14} /> Replace tape
            </button>
            {tape.status === 'cast' && (
              <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.55)', lineHeight: 1.4 }}>
                Heads up — your tape is already in the bracket. Replacing it swaps the video the audience sees.
              </div>
            )}
          </div>
        </div>
      )}

      {/* upload / replace form */}
      {editing && (
        <div
          style={{
            borderRadius: 16,
            border: '1px solid rgba(255,255,255,.12)',
            background: 'rgba(255,255,255,.04)',
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          {/* title */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <label style={{ ...kicker, color: 'rgba(255,255,255,.5)' }}>Tape title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 80))}
              maxLength={80}
              placeholder="e.g. Cold read — Act II monologue"
              style={{
                boxSizing: 'border-box',
                width: '100%',
                background: 'rgba(0,0,0,.28)',
                border: '1px solid rgba(255,255,255,.14)',
                borderRadius: 11,
                padding: '12px 13px',
                color: 'var(--hj-ink)',
                fontFamily: 'var(--hj-font-ui)',
                fontSize: 14,
                outline: 'none',
              }}
            />
          </div>

          {/* file picker */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <label style={{ ...kicker, color: 'rgba(255,255,255,.5)' }}>Your tape</label>
            <input
              ref={fileRef}
              type="file"
              accept="video/*"
              onChange={(e) => pickFile(e.target.files?.[0] || null)}
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              style={{
                cursor: busy ? 'default' : 'pointer',
                border: '1px dashed rgba(255,255,255,.24)',
                background: 'rgba(255,255,255,.03)',
                color: file ? 'var(--hj-ink)' : 'var(--hj-ink-2)',
                borderRadius: 12,
                padding: 16,
                fontFamily: 'var(--hj-font-ui)',
                fontWeight: 600,
                fontSize: 13.5,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 9,
              }}
            >
              <Video size={16} color={file ? GREEN : 'rgba(255,255,255,.6)'} />
              {file ? file.name : 'Choose a video…'}
            </button>
          </div>

          {/* progress */}
          {busy && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ width: '100%', height: 8, borderRadius: 999, background: 'rgba(255,255,255,.08)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${progress}%`, borderRadius: 999, background: 'linear-gradient(90deg,#f286c9,#ffd600)', transition: 'width .2s linear' }} />
              </div>
              <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,.55)', textAlign: 'center' }}>
                {progress < 100 ? `Uploading… ${progress}%` : 'Finishing up…'}
              </span>
            </div>
          )}

          {formError && <div style={{ fontSize: 12.5, color: '#ffb9c6' }}>{formError}</div>}

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={submit}
              disabled={busy}
              style={{
                flex: 1,
                cursor: busy ? 'default' : 'pointer',
                border: 'none',
                borderRadius: 13,
                padding: 15,
                fontFamily: 'var(--hj-font-ui)',
                fontWeight: 700,
                fontSize: 15,
                color: '#150e20',
                background: 'linear-gradient(90deg,#ffe566,#ffd600)',
                opacity: busy ? 0.6 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 9,
              }}
            >
              {busy ? <Loader2 size={17} className="animate-spin" /> : <Upload size={17} />}
              {tape ? 'Replace my tape' : 'Submit my tape'}
            </button>
            {tape && (
              <button
                onClick={() => {
                  setEditing(false);
                  setFile(null);
                  setFormError(null);
                }}
                disabled={busy}
                style={{
                  cursor: busy ? 'default' : 'pointer',
                  border: '1px solid rgba(255,255,255,.18)',
                  background: 'transparent',
                  color: 'var(--hj-ink-2)',
                  borderRadius: 13,
                  padding: '15px 18px',
                  fontFamily: 'var(--hj-font-ui)',
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,.45)', fontSize: 11.5, marginTop: 2 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: REC }} /> Free to enter — no submission fees, ever.
      </div>
    </Wrap>
  );
};

export default GameShowSubmitPage;
